import * as stylex from "@stylexjs/stylex";
import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, space, text } from "@/design/tokens.stylex";

// Pill buttons (DESIGN_SYSTEM.md). A screen has one `primary`; everything else
// is `secondary`, `ghost`, or sits behind More.
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";
const styles = {
  base: {
    k1xSpc: "x3nfvp2",
    kmuXW: "x2lah0s",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "xsuxsxl",
    kZKoxP: "x5esxvw",
    kg3NbH: "x1jbum4n",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x9r1u3d",
    kaIpWk: "x3kktt9",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xtu3avg",
    k63SB2: "xk50ysn",
    kLWn49: "xo5v014",
    khDVqt: "xuxw1ft",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk x1s07b3s",
    kfSwDN: "x87ps6o",
    $$css: true
  },
  sm: {
    kZKoxP: "x1f38ord",
    kg3NbH: "xtft4pz",
    kGuDYH: "x7x6yu3",
    $$css: true
  },
  withIcon: {
    kZCmMZ: "x11iv6b2",
    $$css: true
  },
  primary: {
    kWkggS: "x1wg9yre x25ncql x1bizd19",
    kMwMTN: "xgwkh9f x176rghm",
    $$css: true
  },
  secondary: {
    kWkggS: "xlcwlql x1v4wwsj",
    kMwMTN: "xgwkh9f x176rghm",
    $$css: true
  },
  ghost: {
    kWkggS: "xjbqb8w x1ki36sz",
    kMwMTN: "x1e3avs9 x67ju0b x176rghm",
    $$css: true
  },
  danger: {
    kWkggS: "x8gmtw2 x7hxua0 x1bizd19",
    kMwMTN: "x1sen6cn x176rghm",
    $$css: true
  },
  icon: {
    k1xSpc: "x3nfvp2",
    kmuXW: "x2lah0s",
    $$css: true
  },
  iconButton: {
    kzqmXN: "xjl3xoe",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  iconButtonSm: {
    kzqmXN: "xispsfe",
    $$css: true
  }
};
export interface PillStyleOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  withIcon?: boolean;
  xstyle?: stylex.StyleXStyles;
}

/** The pill's class names, for a link or any element that should look like a button. */
export function pillProps({
  variant = "secondary",
  size = "md",
  withIcon = false,
  xstyle
}: PillStyleOptions = {}) {
  return stylex.props(styles.base, recipes.focusRing, recipes.transition, size === "sm" && styles.sm, withIcon && styles.withIcon, styles[variant], xstyle);
}
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "style"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** A leading icon, 14–16px. */
  icon?: ReactNode;
  xstyle?: stylex.StyleXStyles;
  children: ReactNode;
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = "secondary",
  size = "md",
  icon,
  xstyle,
  children,
  type = "button",
  ...props
}, ref) {
  return <button ref={ref} type={type} {...props} {...pillProps({
    variant,
    size,
    withIcon: Boolean(icon),
    xstyle
  })}>
      {icon && <span aria-hidden className="x3nfvp2 x2lah0s">
          {icon}
        </span>}
      {children}
    </button>;
});
export default Button;
export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "style" | "children"> {
  /** Required: an icon button has no visible text. */
  "aria-label": string;
  variant?: Exclude<ButtonVariant, "primary">;
  size?: ButtonSize;
  xstyle?: stylex.StyleXStyles;
  children: ReactNode;
}

/** A round button holding one icon: close, More, row actions. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({
  variant = "ghost",
  size = "md",
  xstyle,
  children,
  type = "button",
  ...props
}, ref) {
  return <button ref={ref} type={type} title={props["aria-label"]} {...props} {...pillProps({
    variant,
    size,
    xstyle: [styles.iconButton, size === "sm" && styles.iconButtonSm, xstyle]
  })}>
      {children}
    </button>;
});